import React, { useEffect, useState } from 'react';
import {
  Table, Input, Switch, Tag, Tooltip, message, Button, Modal, Drawer, Form, InputNumber, Space, Popconfirm,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import {
  SearchOutlined, EditOutlined, DeleteOutlined, PlusOutlined, UnorderedListOutlined, ShopOutlined, QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  getLogisticsProviders, createLogisticsProvider, updateLogisticsProvider, deleteLogisticsProvider,
  getServices, createService, updateService, deleteService,
} from '../../../services/logistics';
import type { LogisticsProvider, Service } from '../../../services/logistics';
import LogoUploader from './LogoUploader';

const PAGE_SIZE = 20;

// 电池资质六个字段 -> 一句人类可读的清单，供"支持能力"列的电池标签 Tooltip 用；
// 一个都不支持时返回空字符串，调用方据此决定要不要展示这个标签
const describeBatterySupport = (r: Service): string => {
  const labels: string[] = [];
  if (r.battery_lithium_ion_installed) labels.push('锂离子-安装在设备内');
  if (r.battery_lithium_ion_packed) labels.push('锂离子-随设备未安装');
  if (r.battery_lithium_ion_standalone) labels.push('锂离子-单独电池');
  if (r.battery_lithium_metal_installed) labels.push('锂金属-安装在设备内');
  if (r.battery_lithium_metal_packed) labels.push('锂金属-随设备未安装');
  if (r.battery_lithium_metal_standalone) labels.push('锂金属-单独电池');
  return labels.join('、');
};

// ======================== 承运商名下的物流产品子表（在 Drawer 里展示） ========================

const ServiceSubTable: React.FC<{ provider: LogisticsProvider }> = ({ provider }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Service[]>([]);
  const [editing, setEditing] = useState<Partial<Service> | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getServices({ page_size: 500, logistics_provider: provider.id });
      const body = resp.data as any;
      setData(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
    } catch {
      message.error('获取物流产品失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [provider.id]);

  const openCreate = () => {
    setEditing({});
    form.resetFields();
    form.setFieldsValue({ is_active: true });
  };

  const openEdit = (record: Service) => {
    setEditing(record);
    form.setFieldsValue(record);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteService(id);
      message.success('已删除');
      fetchData();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing?.id) {
        await updateService(editing.id, values);
      } else {
        await createService({ ...values, logistics_provider: provider.id });
      }
      message.success('保存成功');
      setEditing(null);
      fetchData();
    } catch (e: any) {
      if (e?.errorFields) return;
      const detail = e?.response?.data;
      const firstError = detail && typeof detail === 'object' ? Object.values(detail)[0] : null;
      message.error((Array.isArray(firstError) ? firstError[0] : firstError) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<Service> = [
    { title: '产品名称', dataIndex: 'name', width: 180 },
    { title: '产品代码', dataIndex: 'code', width: 160 },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag color="default">停用</Tag>),
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      width: 160,
      ellipsis: true,
      render: (text?: string) => text ? <Tooltip title={text}><span>{text}</span></Tooltip> : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (_: any, r: Service) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>查看/编辑</Button>
          <Popconfirm title="确定删除这个物流产品？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button type="dashed" icon={<PlusOutlined />} onClick={openCreate}>新增物流产品</Button>
      </div>
      <Table<Service>
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          defaultPageSize: PAGE_SIZE,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `共${total}条记录`,
        }}
        size="small"
        scroll={{ x: 1060 }}
        locale={{ emptyText: '该承运商下暂无物流产品' }}
      />

      <Modal
        title={editing?.id ? `编辑物流产品：${editing.name}` : `新增物流产品（${provider.name}）`}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="产品名称" rules={[{ required: true, message: '请填写产品名称' }]}>
            <Input placeholder="如 UPS Ground" />
          </Form.Item>
          <Form.Item name="code" label="产品代码" rules={[{ required: true, message: '请填写产品代码' }]}>
            <Input placeholder="如 UPS_GROUND" />
          </Form.Item>
          <div style={{ marginBottom: 16 }}>
            <Space size={4} align="center" style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 500 }}>电池运输资质</span>
              <Tooltip title="需要对照 USPS/UPS/FedEx 官方电池运输指南逐条核实、人工维护，不是自动生成的。除电池外的其它危险品（香水、压缩气体、化学品等）本公司政策不支持任何物流产品运输，不需要单独配置。">
                <QuestionCircleOutlined style={{ color: '#999', cursor: 'help' }} />
              </Tooltip>
            </Space>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '4px 12px' }} />
                  <th style={{ padding: '4px 12px', fontWeight: 400, color: '#666' }}>安装在设备内</th>
                  <th style={{ padding: '4px 12px', fontWeight: 400, color: '#666' }}>随设备未安装</th>
                  <th style={{ padding: '4px 12px', fontWeight: 400, color: '#666' }}>单独电池</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '4px 12px', color: '#666' }}>锂离子电池</td>
                  <td style={{ padding: '4px 12px', textAlign: 'center' }}><Form.Item name="battery_lithium_ion_installed" valuePropName="checked" noStyle><Switch /></Form.Item></td>
                  <td style={{ padding: '4px 12px', textAlign: 'center' }}><Form.Item name="battery_lithium_ion_packed" valuePropName="checked" noStyle><Switch /></Form.Item></td>
                  <td style={{ padding: '4px 12px', textAlign: 'center' }}><Form.Item name="battery_lithium_ion_standalone" valuePropName="checked" noStyle><Switch /></Form.Item></td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 12px', color: '#666' }}>锂金属电池</td>
                  <td style={{ padding: '4px 12px', textAlign: 'center' }}><Form.Item name="battery_lithium_metal_installed" valuePropName="checked" noStyle><Switch /></Form.Item></td>
                  <td style={{ padding: '4px 12px', textAlign: 'center' }}><Form.Item name="battery_lithium_metal_packed" valuePropName="checked" noStyle><Switch /></Form.Item></td>
                  <td style={{ padding: '4px 12px', textAlign: 'center' }}><Form.Item name="battery_lithium_metal_standalone" valuePropName="checked" noStyle><Switch /></Form.Item></td>
                </tr>
              </tbody>
            </table>
          </div>
          <Space size="middle" wrap>
            <Form.Item name="max_weight_kg" label="最大重量(kg)"><InputNumber min={0} precision={2} /></Form.Item>
            <Form.Item name="max_length_cm" label="最大长(cm)"><InputNumber min={0} precision={2} /></Form.Item>
            <Form.Item name="max_width_cm" label="最大宽(cm)"><InputNumber min={0} precision={2} /></Form.Item>
            <Form.Item name="max_height_cm" label="最大高(cm)"><InputNumber min={0} precision={2} /></Form.Item>
          </Space>
          <Space size="middle" wrap>
            <Form.Item name="estimated_transit_days_min" label="预计时效最短(天)"><InputNumber min={0} /></Form.Item>
            <Form.Item name="estimated_transit_days_max" label="预计时效最长(天)"><InputNumber min={0} /></Form.Item>
          </Space>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontWeight: 500 }}>风险保证金规则</span>
              <Tooltip title="购买运单时按“报价 × 保证金比例”冻结一笔保证金，用于覆盖承运商事后重量/尺寸复核产生的运费调整；算出的金额会用最低/最高保证金裁剪。比例填 0（默认）表示不冻结保证金。">
                <QuestionCircleOutlined style={{ color: '#999', cursor: 'help' }} />
              </Tooltip>
            </div>
            <Space size="middle" wrap>
              <Form.Item name="deposit_rate_percent" label="保证金比例(%)"><InputNumber min={0} max={100} precision={2} /></Form.Item>
              <Form.Item name="deposit_min_amount" label="最低保证金"><InputNumber min={0} precision={2} addonBefore="$" /></Form.Item>
              <Form.Item name="deposit_max_amount" label="最高保证金" tooltip="留空表示不设上限"><InputNumber min={0} precision={2} addonBefore="$" /></Form.Item>
            </Space>
          </div>
          <Form.Item
            name="required_documents"
            label="所需附加资料"
            tooltip="每个产品需要的资料可能完全不同，这里手动写清楚，不是固定选项。比如国际件清关需要收件人身份证信息/购物发票/税号；行李托运渠道需要机票行程单/护照信息等"
          >
            <Input.TextArea rows={3} placeholder="如：需提供收件人身份证正反面照片、购物发票、税号" />
          </Form.Item>
          <Form.Item name="remarks" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ======================== 承运商主表 ========================

const ProviderServiceTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LogisticsProvider[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');

  const [editing, setEditing] = useState<Partial<LogisticsProvider> | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  // 管理物流产品用 Drawer 展示，而不是表格行内展开——避免表格套表格的视觉混乱
  const [managingProvider, setManagingProvider] = useState<LogisticsProvider | null>(null);

  // 点击"已有物流产品"数字时的快速预览弹窗——只读简要信息，跟"管理物流产品"Drawer（可增删改）分开，
  // 只是想扫一眼有哪些产品时不用开整个管理面板
  const [viewingProvider, setViewingProvider] = useState<LogisticsProvider | null>(null);
  const [viewingServices, setViewingServices] = useState<Service[]>([]);
  const [viewingLoading, setViewingLoading] = useState(false);

  const openQuickView = async (provider: LogisticsProvider) => {
    setViewingProvider(provider);
    setViewingLoading(true);
    try {
      const resp = await getServices({ page_size: 500, logistics_provider: provider.id });
      const body = resp.data as any;
      setViewingServices(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
    } catch {
      message.error('获取物流产品失败');
      setViewingServices([]);
    } finally {
      setViewingLoading(false);
    }
  };

  const fetchData = async (p: number, q?: string, ps?: number) => {
    const size = ps || pageSize;
    setLoading(true);
    try {
      const resp = await getLogisticsProviders({ page: p, page_size: size, search: q || undefined });
      const body = resp.data as any;
      setData(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
      setTotal(Number(body?.count ?? 0) || 0);
      setPage(p);
      setPageSize(size);
    } catch {
      message.error('获取承运商列表失败');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(1, ''); }, []);

  const handleSearch = () => fetchData(1, searchText);

  const openCreate = () => {
    setEditing({});
    form.resetFields();
    setLogoFile(null);
    setRemoveLogo(false);
  };

  const openEdit = (record: LogisticsProvider) => {
    setEditing(record);
    form.setFieldsValue(record);
    setLogoFile(null);
    setRemoveLogo(false);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteLogisticsProvider(id);
      message.success('已删除');
      fetchData(page, searchText);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '删除失败（可能仍有物流产品/报价记录关联）');
    }
  };

  const handleSave = async () => {
    try {
      const { logo: _existingLogoUrl, ...values } = await form.validateFields();
      // logo 字段只能是"新上传的文件"或者"完全不传"，绝不能把 openEdit 时 setFieldsValue(record)
      // 带进表单 store 的旧 logo 网址字符串原样提交回去——不管是走下面的 FormData 分支（会和
      // fd.append('logo', logoFile) 撞在同一个 key 上，后端解析直接报"不是文件"），还是走 JSON
      // 分支（DRF 的 ImageField 收到字符串同样会报同一个错），所以在这里统一先解构掉，绝口不提。
      setSaving(true);
      // 只有真的动了 Logo（选了新文件或者标记删除）才用 FormData 提交，
      // 不然普通字段编辑走 JSON 就够了，不用每次都搭个 multipart 请求
      let payload: Partial<LogisticsProvider> | FormData = values;
      if (logoFile || removeLogo) {
        const fd = new FormData();
        Object.entries(values).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          fd.append(key, String(value));
        });
        if (logoFile) {
          fd.append('logo', logoFile);
        } else if (removeLogo) {
          fd.append('remove_logo', 'true');
        }
        payload = fd;
      }
      if (editing?.id) {
        await updateLogisticsProvider(editing.id, payload);
      } else {
        await createLogisticsProvider(payload);
      }
      message.success('保存成功');
      setEditing(null);
      fetchData(page, searchText);
    } catch (e: any) {
      if (e?.errorFields) return;
      const detail = e?.response?.data;
      const firstError = detail && typeof detail === 'object' ? Object.values(detail)[0] : null;
      message.error((Array.isArray(firstError) ? firstError[0] : firstError) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<LogisticsProvider> = [
    {
      title: '承运商名称',
      dataIndex: 'name',
      width: 240,
      // Logo 放在名称前面，不再单独占一列——跟"物流承运商列表"页面是同一个展示模式。
      // 不用 Avatar：它是固定正方形 + object-fit: cover，长方形 Logo 会被裁掉两边；
      // 这里用一个小方块 + object-fit: contain，完整显示整张 Logo（留白不裁剪）。
      render: (text: string, record: LogisticsProvider) => (
        <Space>
          <div
            style={{
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            {record.logo ? (
              <img src={record.logo} alt={text} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <ShopOutlined style={{ color: '#bfbfbf', fontSize: 22 }} />
            )}
          </div>
          <span>{text}</span>
        </Space>
      ),
    },
    { title: '承运商代码', dataIndex: 'code', width: 140 },
    {
      title: '官网',
      dataIndex: 'website',
      width: 150,
      render: (text: string) => text ? <a href={text} target="_blank" rel="noopener noreferrer">访问官网</a> : '-',
    },
    {
      title: '已有物流产品',
      dataIndex: 'service_count',
      width: 110,
      render: (count: number, r: LogisticsProvider) => (
        <Button type="link" size="small" onClick={() => openQuickView(r)}>{count ?? 0}</Button>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      width: 200,
      ellipsis: true,
      render: (text?: string) => text ? <Tooltip title={text}><span>{text}</span></Tooltip> : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 230,
      fixed: 'right',
      render: (_: any, r: LogisticsProvider) => (
        <Space>
          <Button type="link" size="small" icon={<UnorderedListOutlined />} onClick={() => setManagingProvider(r)}>管理物流产品</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除这个承运商？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12, color: '#999' }}>
        承运商是最终服务的实际提供者（如 UPS、USPS），点击"管理产品"可以管理它名下具体的物流产品（如 UPS Ground），并设置每种物流产品的运费风险保证金规则。
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Space>
          <Input
            placeholder="搜索承运商名称/代码"
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
          <Button onClick={handleSearch}>搜索</Button>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchData(page, searchText)}>刷新</Button>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增承运商</Button>
      </div>

      <Table<LogisticsProvider>
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        onChange={(p) => fetchData(Number(p.current || 1), searchText, Number(p.pageSize || pageSize))}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t, range) => `共${t}条记录，显示${range[0]}-${range[1]}条`,
        } as TablePaginationConfig}
        scroll={{ x: 1090 }}
      />

      <Modal
        title={editing?.id ? `编辑承运商：${editing.name}` : '新增承运商'}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="承运商名称" rules={[{ required: true, message: '请填写承运商名称' }]}>
            <Input placeholder="如 UPS" />
          </Form.Item>
          <Form.Item name="code" label="承运商代码" rules={[{ required: true, message: '请填写承运商代码' }]}>
            <Input placeholder="如 UPS" />
          </Form.Item>
          <Form.Item name="website" label="官网">
            <Input placeholder="https://" />
          </Form.Item>
          <Form.Item label="Logo">
            <LogoUploader
              existingUrl={editing?.logo}
              stagedFile={logoFile}
              onStagedFileChange={setLogoFile}
              markedForRemoval={removeLogo}
              onMarkedForRemovalChange={setRemoveLogo}
            />
          </Form.Item>
          <Form.Item name="remarks" label="备注">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={managingProvider ? `物流产品管理：${managingProvider.name}（${managingProvider.code}）` : ''}
        open={!!managingProvider}
        onClose={() => setManagingProvider(null)}
        width={860}
        destroyOnClose
      >
        {managingProvider && <ServiceSubTable provider={managingProvider} />}
      </Drawer>

      <Modal
        title={viewingProvider ? `物流产品一览：${viewingProvider.name}（${viewingProvider.code}）` : ''}
        open={!!viewingProvider}
        onCancel={() => setViewingProvider(null)}
        footer={<Button onClick={() => setViewingProvider(null)}>关闭</Button>}
        width={960}
        destroyOnClose
      >
        <Table<Service>
          columns={[
            { title: '产品名称', dataIndex: 'name', width: 220 },
            { title: '产品代码', dataIndex: 'code', width: 180 },
            {
              title: '电池资质',
              key: 'capabilities',
              render: (_: any, r: Service) => (
                <Space size={4} wrap>
                  {!!describeBatterySupport(r) && (
                    <Tooltip title={describeBatterySupport(r)}>
                      <Tag>电池</Tag>
                    </Tooltip>
                  )}
                </Space>
              ),
            },
            {
              title: '所需附加资料',
              dataIndex: 'required_documents',
              width: 140,
              ellipsis: true,
              render: (text?: string) => text ? <Tooltip title={text}><span>{text}</span></Tooltip> : '-',
            },
            {
              title: '备注',
              dataIndex: 'remarks',
              width: 120,
              ellipsis: true,
              render: (text?: string) => text ? <Tooltip title={text}><span>{text}</span></Tooltip> : '-',
            },
            {
              title: '状态',
              dataIndex: 'is_active',
              width: 70,
              render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag color="default">停用</Tag>),
            },
          ]}
          dataSource={viewingServices}
          rowKey="id"
          loading={viewingLoading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t, range) => `共${t}条记录，显示${range[0]}-${range[1]}条`,
          }}
          size="small"
          scroll={{ x: 900 }}
          locale={{ emptyText: '该承运商下暂无物流产品' }}
        />
      </Modal>
    </div>
  );
};

export default ProviderServiceTab;
